import { useCallback, useState } from "react";
import InfoMessageContext from "../../contexts/InfoMessageContext";
import SnackBar from "./components/SnackBar";
import type { SnackbarProps } from "@mui/material";

export type InfoMessageType = "info" | "success" | "warning" | "error";

export interface InfoMessage {
  message: string;
  type: InfoMessageType;
  description?: string;
  /** If provided, this will be rendered as the action of the SnackBar. It can be used to provide custom buttons or nodes as actions for the info message. */
  action?: SnackbarProps["action"];
  /** If not provided, the SnackBar will auto-hide after 6000ms. */
  autoHideDuration?: SnackbarProps["autoHideDuration"];
}

type Info = Omit<InfoMessage, "type">;

export type SendInfoMessageFn = (info: Info, type?: InfoMessage["type"]) => void;

interface InfoMessageProviderProps {
  children: React.ReactNode;
}

function InfoMessageProvider({ children }: InfoMessageProviderProps) {
  const [info, setInfo] = useState<InfoMessage | undefined>(undefined);

  const sendInfoMessage = useCallback<SendInfoMessageFn>(
    ({ message, description, action, autoHideDuration }, type = "info") => {
      setInfo({ message, type, description, action, autoHideDuration });
    },
    []
  );

  const handleClose = useCallback(() => setInfo(undefined), []);

  return (
    <InfoMessageContext.Provider value={sendInfoMessage}>
      {children}
      {info && (
        <SnackBar
          message={info.message}
          description={info.description}
          type={info.type}
          onClose={handleClose}
          action={info.action}
          autoHideDuration={info.autoHideDuration}
        />
      )}
    </InfoMessageContext.Provider>
  );
}

export default InfoMessageProvider;
